<template>
  <div>
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.activity_type') }}</label>
        <v-select
          v-model="localActivityType"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="activityTypeSelectOptions"
          class="w-100"
          label="label"
          :reduce="(item) => (item != null && item.value != null ? item.value : item)"
          clearable
        />
      </b-col>
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.business_division') }}</label>
        <v-select
          v-model="localSectorIds"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="sectorSelectOptions"
          class="w-100"
          label="label"
          :reduce="reduceOptionToValue"
          :multiple="true"
          clearable
        />
      </b-col>
    </b-row>
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.federal_entity') }}</label>
        <v-select
          v-model="localRegionIds"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="regionSelectOptions"
          class="w-100"
          label="label"
          :reduce="reduceOptionToValue"
          :multiple="true"
          clearable
        />
      </b-col>
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.rems') }}</label>
        <b-form-input
          v-model="localRems"
          type="text"
          :placeholder="$t('planning.rems')"
        />
      </b-col>
    </b-row>
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.property_name') }}</label>
        <b-form-input
          v-model="localUnitName"
          type="text"
          :placeholder="$t('planning.property_name')"
        />
      </b-col>
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.property_type') }}</label>
        <b-form-input
          v-model="localFacilityCategory"
          type="text"
          :placeholder="$t('planning.property_type')"
        />
      </b-col>
    </b-row>
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.status') }}</label>
        <v-select
          v-model="localStatus"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="statusSelectOptions"
          class="w-100"
          label="label"
          :reduce="reduceOptionToValue"
          clearable
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.fecha_inicio') }}</label>
        <flat-pickr
          class="form-control"
          :config="datePickerConfig"
          :value="fechaInicioFilter"
          @on-change="onFechaInicioChange"
        />
      </b-col>
      <b-col cols="12" md="6" class="mb-2">
        <label class="text-capitalize">{{ $t('planning.fecha_fin') }}</label>
        <flat-pickr
          :key="'fecha-fin-' + (fechaInicioFilter || '')"
          class="form-control"
          :config="dateToPickerConfig"
          :value="fechaFinFilter"
          @on-change="onFechaFinChange"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { BRow, BCol, BFormInput } from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import store from '@/store'
import popperHelper from '@/libs/popper-helper'

const ACTIVITY_TYPE_SLUGS = [
  { value: 'risk', labelKey: 'planning.evaluacion_riesgo' },
  { value: 'operational_procedure', labelKey: 'planning.ejercicio_operativo' },
  { value: 'simulacrum', labelKey: 'planning.simulacro' },
  { value: 'security_self_audit', labelKey: 'planning.auto_evaluacion_seguridad' },
]

const ACTIVITY_TYPE_LABEL_FALLBACKS = {
  'planning.evaluacion_riesgo': 'Evaluación de riesgo',
  'planning.ejercicio_operativo': 'Ejercicio operativo',
  'planning.simulacro': 'Simulacro',
  'planning.auto_evaluacion_seguridad': 'Auto evaluación de seguridad',
}

const STATUS_FILTER_CODES = ['draft', 'review', 'additional', 'approved', 'released']

const datePickerConfig = { dateFormat: 'Y-m-d', allowInput: false }

function normalizeDateToYmd(selectedDates, val) {
  if (Array.isArray(selectedDates) && selectedDates.length > 0) {
    const d = selectedDates[0]
    if (d instanceof Date) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    }
  }
  if (val instanceof Date) {
    const y = val.getFullYear()
    const m = String(val.getMonth() + 1).padStart(2, '0')
    const day = String(val.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  if (typeof val === 'string' && val.length >= 10) {
    return val.slice(0, 10)
  }
  return val == null ? null : String(val)
}

function reduceOptionToValue(item) {
  if (item == null) return item
  return item.value != null ? item.value : item.id
}

export default {
  name: 'FiltersPlanning',
  components: {
    BRow,
    BCol,
    BFormInput,
    flatPickr,
    vSelect,
  },
  props: {
    activityTypeFilter: { type: [String, null], default: null },
    sectorIdsFilter: { type: Array, default: () => [] },
    regionIdsFilter: { type: Array, default: () => [] },
    remsFilter: { type: [String, null], default: null },
    unitNameFilter: { type: [String, null], default: null },
    facilityCategoryFilter: { type: [String, null], default: null },
    fechaInicioFilter: { type: [String, null], default: null },
    fechaFinFilter: { type: [String, null], default: null },
    statusFilter: { type: [String, null], default: null },
  },
  data() {
    return {
      datePickerConfig,
      normalizeDateToYmd,
      reduceOptionToValue,
    }
  },
  computed: {
    activityTypeSelectOptions() {
      return ACTIVITY_TYPE_SLUGS.map((item) => {
        const translated = this.$t(item.labelKey)
        const label = translated !== item.labelKey ? translated : (ACTIVITY_TYPE_LABEL_FALLBACKS[item.labelKey] || item.labelKey)
        return { value: item.value, label }
      })
    },
    statusSelectOptions() {
      return STATUS_FILTER_CODES.map((code) => {
        const key = `planning.status_${code}`
        const translated = this.$t(key)
        const label = translated !== key ? translated : code
        return { value: code, label }
      })
    },
    sectorSelectOptions() {
      const raw = store.getters['sector/getSelectedItems'] || []
      return raw.filter((item) => item != null).map((item) => ({
        value: item.value != null ? item.value : item.id,
        label: item.label != null ? item.label : (item.name != null ? item.name : String(item.value != null ? item.value : item.id)),
      }))
    },
    regionSelectOptions() {
      const raw = store.getters['region/getSelectedItems'] || []
      return raw.filter((item) => item != null).map((item) => ({
        value: item.value != null ? item.value : item.id,
        label: item.label != null ? item.label : (item.name != null ? item.name : String(item.value != null ? item.value : item.id)),
      }))
    },
    localActivityType: {
      get() {
        return this.activityTypeFilter != null && this.activityTypeFilter !== '' ? this.activityTypeFilter : null
      },
      set(v) {
        this.$emit('update:activityTypeFilter', v != null && v !== '' ? v : null)
      },
    },
    localSectorIds: {
      get() {
        return Array.isArray(this.sectorIdsFilter) ? this.sectorIdsFilter : []
      },
      set(v) {
        this.$emit('update:sectorIdsFilter', Array.isArray(v) ? v : [])
      },
    },
    localRegionIds: {
      get() {
        return Array.isArray(this.regionIdsFilter) ? this.regionIdsFilter : []
      },
      set(v) {
        this.$emit('update:regionIdsFilter', Array.isArray(v) ? v : [])
      },
    },
    localRems: {
      get() {
        return this.remsFilter != null && this.remsFilter !== '' ? this.remsFilter : ''
      },
      set(v) {
        this.$emit('update:remsFilter', v != null && String(v).trim() !== '' ? String(v).trim() : null)
      },
    },
    localUnitName: {
      get() {
        return this.unitNameFilter != null && this.unitNameFilter !== '' ? this.unitNameFilter : ''
      },
      set(v) {
        this.$emit('update:unitNameFilter', v != null && String(v).trim() !== '' ? String(v).trim() : null)
      },
    },
    localFacilityCategory: {
      get() {
        return this.facilityCategoryFilter != null && this.facilityCategoryFilter !== '' ? this.facilityCategoryFilter : ''
      },
      set(v) {
        this.$emit('update:facilityCategoryFilter', v != null && String(v).trim() !== '' ? String(v).trim() : null)
      },
    },
    localStatus: {
      get() {
        return this.statusFilter != null && this.statusFilter !== '' ? this.statusFilter : null
      },
      set(v) {
        this.$emit('update:statusFilter', v != null && String(v).trim() !== '' ? String(v).trim() : null)
      },
    },
    dateToPickerConfig() {
      const base = { dateFormat: 'Y-m-d', allowInput: false }
      if (!this.fechaInicioFilter) return base
      const fromStr = String(this.fechaInicioFilter).slice(0, 10)
      if (/^\d{4}-\d{2}-\d{2}$/.test(fromStr)) {
        base.minDate = new Date(`${fromStr}T00:00:00`)
      }
      return base
    },
  },
  mounted() {
    store.dispatch('sector/getAll')
    store.dispatch('region/getAll')
  },
  methods: {
    onFechaInicioChange(selectedDates, val) {
      this.$emit('update:fechaInicioFilter', this.normalizeDateToYmd(selectedDates, val))
    },
    onFechaFinChange(selectedDates, val) {
      this.$emit('update:fechaFinFilter', this.normalizeDateToYmd(selectedDates, val))
    },
  },
  setup() {
    return {
      withPopper: popperHelper.withPopper,
    }
  },
}
</script>
